import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { MouseEvent as ReactMouseEvent, TouchEvent as ReactTouchEvent, RefObject } from 'react';

type UseAnalogClockParams = {
	hours: number;
	minutes: number;
	onTimeChange?: (hours: number, minutes: number) => void;
};

type UseAnalogClockResult = {
	clockRef: RefObject<SVGSVGElement | null>;
	angles: {
		hour: number;
		minute: number;
	};
	isDraggingHour: boolean;
	isDraggingMinute: boolean;
	handleHourMouseDown: (event: ReactMouseEvent) => void;
	handleMinuteMouseDown: (event: ReactMouseEvent) => void;
	handleHourTouchStart: (event: ReactTouchEvent) => void;
	handleMinuteTouchStart: (event: ReactTouchEvent) => void;
};

const FULL_TURN = 360;
const MINUTES_PER_HOUR = 60;
const HOURS_ON_CLOCK = 12;
const MINUTE_DEGREE = FULL_TURN / MINUTES_PER_HOUR; // 6 degrees
const HOUR_DEGREE = FULL_TURN / HOURS_ON_CLOCK; // 30 degrees

const useAnalogClock = ({ hours, minutes, onTimeChange }: UseAnalogClockParams): UseAnalogClockResult => {
	const clockRef = useRef<SVGSVGElement>(null);
	const [isDraggingHour, setIsDraggingHour] = useState(false);
	const [isDraggingMinute, setIsDraggingMinute] = useState(false);

	// Normalize hours: 0 or 12 should both be displayed as 12 on analog clock
	const normalizedHours = hours === 0 ? HOURS_ON_CLOCK : hours;

	const isInteractive = Boolean(onTimeChange);
	const previousMinuteRef = useRef(minutes);
	const hoursRef = useRef(normalizedHours);
	const rotationRef = useRef({
		minuteTurns: 0,
		hourTurns: 0,
		lastMinute: minutes,
		lastHour: normalizedHours,
		lastHourAngleRaw: (normalizedHours % HOURS_ON_CLOCK) * HOUR_DEGREE + minutes * 0.5,
		continuousHourAngle: null as number | null,
	});

	const angles = useMemo(() => {
		const { minuteTurns, hourTurns, continuousHourAngle } = rotationRef.current;

		const minuteAngle = minutes * MINUTE_DEGREE + minuteTurns * FULL_TURN;
		const computedHourBase = (hours % HOURS_ON_CLOCK) * HOUR_DEGREE + minutes * 0.5 + hourTurns * FULL_TURN;
		const hourAngle = continuousHourAngle ?? computedHourBase;

		return {
			minute: minuteAngle,
			hour: hourAngle,
		};
	}, [hours, minutes]);

	const calculateAngle = useCallback(
		(event: MouseEvent | TouchEvent | React.MouseEvent | React.TouchEvent, svg: SVGSVGElement) => {
			const rect = svg.getBoundingClientRect();
			const centerX = rect.left + rect.width / 2;
			const centerY = rect.top + rect.height / 2;

			// Handle both mouse and touch events
			const clientX = 'touches' in event ? event.touches[0]?.clientX ?? 0 : event.clientX;
			const clientY = 'touches' in event ? event.touches[0]?.clientY ?? 0 : event.clientY;

			const deltaX = clientX - centerX;
			const deltaY = clientY - centerY;

			let angle = (Math.atan2(deltaY, deltaX) * 180) / Math.PI + 90;
			if (angle < 0) angle += FULL_TURN;

			return angle;
		},
		[],
	);

	const angleToMinutes = useCallback((angle: number): number => {
		const rawMinutes = Math.round(angle / MINUTE_DEGREE) % MINUTES_PER_HOUR;
		const snappedToFive = Math.round(rawMinutes / 5) * 5;
		return snappedToFive % MINUTES_PER_HOUR;
	}, []);

	const handleHourMouseDown = useCallback(
		(event: ReactMouseEvent) => {
			if (!isInteractive) return;
			event.preventDefault();
			event.stopPropagation();
			setIsDraggingHour(true);
		},
		[isInteractive],
	);

	const handleMinuteMouseDown = useCallback(
		(event: ReactMouseEvent) => {
			if (!isInteractive) return;
			event.preventDefault();
			event.stopPropagation();
			setIsDraggingMinute(true);
		},
		[isInteractive],
	);

	const handleMouseMove = useCallback(
		(event: MouseEvent) => {
			if (!isInteractive || !clockRef.current || !onTimeChange) return;

			if (isDraggingHour) {
				const angle = calculateAngle(event, clockRef.current);
				const { hourTurns, lastHourAngleRaw } = rotationRef.current;
				let updatedHourTurns = hourTurns;

				if (
					lastHourAngleRaw !== undefined &&
					lastHourAngleRaw >= 300 &&
					angle <= 60 &&
					angle !== lastHourAngleRaw
				) {
					updatedHourTurns += 1;
				} else if (
					lastHourAngleRaw !== undefined &&
					lastHourAngleRaw <= 60 &&
					angle >= 300 &&
					angle !== lastHourAngleRaw
				) {
					updatedHourTurns -= 1;
				}

				const totalHourAngle = angle + updatedHourTurns * FULL_TURN;
				const newHoursRaw = Math.round(totalHourAngle / HOUR_DEGREE);
				const wrappedHour = ((newHoursRaw % HOURS_ON_CLOCK) + HOURS_ON_CLOCK) % HOURS_ON_CLOCK;
				const newHours = wrappedHour === 0 ? HOURS_ON_CLOCK : wrappedHour;
				const alignedHourAngle =
					(newHours % HOURS_ON_CLOCK) * HOUR_DEGREE + minutes * 0.5 + updatedHourTurns * FULL_TURN;

				rotationRef.current = {
					...rotationRef.current,
					hourTurns: updatedHourTurns,
					lastHour: newHours,
					lastHourAngleRaw: angle,
					continuousHourAngle: alignedHourAngle,
				};
				hoursRef.current = newHours;
				onTimeChange(newHours, minutes);
			} else if (isDraggingMinute) {
				const angle = calculateAngle(event, clockRef.current);
				const newMinutes = angleToMinutes(angle);
				let updatedHours = hoursRef.current;
				const prevMinutes = previousMinuteRef.current;

				if (prevMinutes >= 55 && newMinutes <= 5 && newMinutes !== prevMinutes) {
					rotationRef.current = {
						...rotationRef.current,
						minuteTurns: rotationRef.current.minuteTurns + 1,
					};
					updatedHours = updatedHours === HOURS_ON_CLOCK ? 1 : updatedHours + 1;
				} else if (prevMinutes <= 5 && newMinutes >= 55 && newMinutes !== prevMinutes) {
					rotationRef.current = {
						...rotationRef.current,
						minuteTurns: rotationRef.current.minuteTurns - 1,
					};
					updatedHours = updatedHours === 1 ? HOURS_ON_CLOCK : updatedHours - 1;
				}

				previousMinuteRef.current = newMinutes;
				const { hourTurns, lastHourAngleRaw } = rotationRef.current;
				let updatedHourTurns = hourTurns;
				const currentHourAngleRaw = (updatedHours % HOURS_ON_CLOCK) * HOUR_DEGREE + newMinutes * 0.5;

				if (
					lastHourAngleRaw !== undefined &&
					lastHourAngleRaw >= 300 &&
					currentHourAngleRaw <= 60 &&
					currentHourAngleRaw !== lastHourAngleRaw
				) {
					updatedHourTurns += 1;
				} else if (
					lastHourAngleRaw !== undefined &&
					lastHourAngleRaw <= 60 &&
					currentHourAngleRaw >= 300 &&
					currentHourAngleRaw !== lastHourAngleRaw
				) {
					updatedHourTurns -= 1;
				}

				rotationRef.current = {
					...rotationRef.current,
					lastMinute: newMinutes,
					hourTurns: updatedHourTurns,
					lastHour: updatedHours,
					lastHourAngleRaw: currentHourAngleRaw,
					continuousHourAngle:
						(updatedHours % HOURS_ON_CLOCK) * HOUR_DEGREE + newMinutes * 0.5 + updatedHourTurns * FULL_TURN,
				};
				hoursRef.current = updatedHours;
				onTimeChange(updatedHours, newMinutes);
			}
		},
		[isInteractive, isDraggingHour, isDraggingMinute, calculateAngle, angleToMinutes, onTimeChange, minutes],
	);

	const handleMouseUp = useCallback(() => {
		if (!isInteractive) return;
		setIsDraggingHour(false);
		setIsDraggingMinute(false);
	}, [isInteractive]);

	const handleHourTouchStart = useCallback(
		(event: ReactTouchEvent) => {
			if (!isInteractive) return;
			event.preventDefault();
			event.stopPropagation();
			setIsDraggingHour(true);
		},
		[isInteractive],
	);

	const handleMinuteTouchStart = useCallback(
		(event: ReactTouchEvent) => {
			if (!isInteractive) return;
			event.preventDefault();
			event.stopPropagation();
			setIsDraggingMinute(true);
		},
		[isInteractive],
	);

	const handleTouchMove = useCallback(
		(event: TouchEvent) => {
			if (!isInteractive || !clockRef.current || !onTimeChange || event.touches.length === 0) return;

			if (isDraggingHour) {
				const angle = calculateAngle(event, clockRef.current);
				const { hourTurns, lastHourAngleRaw } = rotationRef.current;
				let updatedHourTurns = hourTurns;

				if (
					lastHourAngleRaw !== undefined &&
					lastHourAngleRaw >= 300 &&
					angle <= 60 &&
					angle !== lastHourAngleRaw
				) {
					updatedHourTurns += 1;
				} else if (
					lastHourAngleRaw !== undefined &&
					lastHourAngleRaw <= 60 &&
					angle >= 300 &&
					angle !== lastHourAngleRaw
				) {
					updatedHourTurns -= 1;
				}

				const totalHourAngle = angle + updatedHourTurns * FULL_TURN;
				const newHoursRaw = Math.round(totalHourAngle / HOUR_DEGREE);
				const wrappedHour = ((newHoursRaw % HOURS_ON_CLOCK) + HOURS_ON_CLOCK) % HOURS_ON_CLOCK;
				const newHours = wrappedHour === 0 ? HOURS_ON_CLOCK : wrappedHour;
				const alignedHourAngle =
					(newHours % HOURS_ON_CLOCK) * HOUR_DEGREE + minutes * 0.5 + updatedHourTurns * FULL_TURN;

				rotationRef.current = {
					...rotationRef.current,
					hourTurns: updatedHourTurns,
					lastHour: newHours,
					lastHourAngleRaw: angle,
					continuousHourAngle: alignedHourAngle,
				};
				hoursRef.current = newHours;
				onTimeChange(newHours, minutes);
			} else if (isDraggingMinute) {
				const angle = calculateAngle(event, clockRef.current);
				const newMinutes = angleToMinutes(angle);
				let updatedHours = hoursRef.current;
				const prevMinutes = previousMinuteRef.current;

				if (prevMinutes >= 55 && newMinutes <= 5 && newMinutes !== prevMinutes) {
					rotationRef.current = {
						...rotationRef.current,
						minuteTurns: rotationRef.current.minuteTurns + 1,
					};
					updatedHours = updatedHours === HOURS_ON_CLOCK ? 1 : updatedHours + 1;
				} else if (prevMinutes <= 5 && newMinutes >= 55 && newMinutes !== prevMinutes) {
					rotationRef.current = {
						...rotationRef.current,
						minuteTurns: rotationRef.current.minuteTurns - 1,
					};
					updatedHours = updatedHours === 1 ? HOURS_ON_CLOCK : updatedHours - 1;
				}

				previousMinuteRef.current = newMinutes;
				const { hourTurns, lastHourAngleRaw } = rotationRef.current;
				let updatedHourTurns = hourTurns;
				const currentHourAngleRaw = (updatedHours % HOURS_ON_CLOCK) * HOUR_DEGREE + newMinutes * 0.5;

				if (
					lastHourAngleRaw !== undefined &&
					lastHourAngleRaw >= 300 &&
					currentHourAngleRaw <= 60 &&
					currentHourAngleRaw !== lastHourAngleRaw
				) {
					updatedHourTurns += 1;
				} else if (
					lastHourAngleRaw !== undefined &&
					lastHourAngleRaw <= 60 &&
					currentHourAngleRaw >= 300 &&
					currentHourAngleRaw !== lastHourAngleRaw
				) {
					updatedHourTurns -= 1;
				}

				rotationRef.current = {
					...rotationRef.current,
					lastMinute: newMinutes,
					hourTurns: updatedHourTurns,
					lastHour: updatedHours,
					lastHourAngleRaw: currentHourAngleRaw,
					continuousHourAngle:
						(updatedHours % HOURS_ON_CLOCK) * HOUR_DEGREE + newMinutes * 0.5 + updatedHourTurns * FULL_TURN,
				};
				hoursRef.current = updatedHours;
				onTimeChange(updatedHours, newMinutes);
			}
		},
		[isInteractive, isDraggingHour, isDraggingMinute, calculateAngle, angleToMinutes, onTimeChange, minutes],
	);

	const handleTouchEnd = useCallback(() => {
		if (!isInteractive) return;
		setIsDraggingHour(false);
		setIsDraggingMinute(false);
	}, [isInteractive]);

	useEffect(() => {
		const prevMinutes = previousMinuteRef.current;
		const prevHours = hoursRef.current;

		const { minuteTurns, lastMinute } = rotationRef.current;
		let updatedMinuteTurns = minuteTurns;
		if (lastMinute !== undefined && minutes !== lastMinute) {
			if (lastMinute >= 55 && minutes <= 5) {
				updatedMinuteTurns += 1;
			} else if (lastMinute <= 5 && minutes >= 55) {
				updatedMinuteTurns -= 1;
			}
		}

		rotationRef.current = {
			...rotationRef.current,
			minuteTurns: updatedMinuteTurns,
			lastMinute: minutes,
			...(!isDraggingHour && (minutes !== prevMinutes || normalizedHours !== prevHours)
				? {
						lastHourAngleRaw: (normalizedHours % HOURS_ON_CLOCK) * HOUR_DEGREE + minutes * 0.5,
						continuousHourAngle:
							(normalizedHours % HOURS_ON_CLOCK) * HOUR_DEGREE +
							minutes * 0.5 +
							rotationRef.current.hourTurns * FULL_TURN,
				  }
				: {}),
		};

		previousMinuteRef.current = minutes;
		hoursRef.current = normalizedHours;
	}, [minutes, hours, normalizedHours, isDraggingHour]);

	useEffect(() => {
		if (!isInteractive) return;
		if (isDraggingHour || isDraggingMinute) {
			window.addEventListener('mousemove', handleMouseMove);
			window.addEventListener('mouseup', handleMouseUp);
			window.addEventListener('touchmove', handleTouchMove);
			window.addEventListener('touchend', handleTouchEnd);

			return () => {
				window.removeEventListener('mousemove', handleMouseMove);
				window.removeEventListener('mouseup', handleMouseUp);
				window.removeEventListener('touchmove', handleTouchMove);
				window.removeEventListener('touchend', handleTouchEnd);
			};
		}
	}, [
		isInteractive,
		isDraggingHour,
		isDraggingMinute,
		handleMouseMove,
		handleMouseUp,
		handleTouchMove,
		handleTouchEnd,
	]);

	return {
		clockRef,
		angles,
		isDraggingHour,
		isDraggingMinute,
		handleHourMouseDown,
		handleMinuteMouseDown,
		handleHourTouchStart,
		handleMinuteTouchStart,
	};
};

export default useAnalogClock;
